import { useChatStream } from "@/contexts/chat-stream-context";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { ChatRequestMode, CompanyDTO, ConversationMessagePromptInfo } from "@shaarp/common";
import { CSSProperties, useEffect, useRef, useState } from "react";
import { ChatCompanyHome } from "./chat-company-home";
import { MemoryMessageToSend } from "./chat-conversation";
import { ChatComposerDisclaimer } from "./chat-dialog-primitives";
import { ChatNewGreeting } from "./chat-new-greeting";
import { ChatNewMobileHome } from "@/components/chat/chat-new-mobile-home";
import { ChatPromptsSuggestions } from "./chat-prompts-suggestions";
import { ChatRecentCompanies } from "./chat-recent-companies";
import { ChatRequestModePills } from "./chat-request-mode-pills";
import { ChatTextInput, ChatTextInputRef } from "./chat-text-input";
import {
  COMPOSER_GLIDE_DURATION_MS,
  useComposerGlide,
} from "./use-composer-glide";

/**
 * When each block of the welcome screen starts rising, from the top down. The
 * blocks that stagger their own items — the mode pills, the tactic cards, the
 * recent company chips — start that stagger at their offset here. Without it
 * every block opened its cascade at once, and the screen popped in as a whole.
 */
const HOME_CASCADE_START_MS = {
  greeting: 0,
  modes: 40,
  composer: 140,
  tactics: 180,
  recentCompanies: 280,
} as const;

/** Rise of each element: quicker than the 0.4–0.5s used elsewhere. */
const HOME_CASCADE_DURATION_MS = 250;

/** Gap between two items of a block, against 60ms elsewhere. */
const HOME_CASCADE_STEP_MS = 40;

/**
 * Past the rise of the last chip of the first recent companies page. From then
 * on the offsets are dropped, so a block that mounts late — tactics or chips
 * whose request lands after the cascade, a next page of chips — rises as soon
 * as it arrives instead of waiting out a delay meant for the first paint.
 */
const HOME_CASCADE_END_MS = 800;

export function ChatNew({
  onMessageSent,
  streamInProgress = false,
  companyTargeted = null,
  draftToRestore,
  onDraftRestored,
}: {
  onMessageSent: (params: MemoryMessageToSend) => void;
  streamInProgress?: boolean;
  /**
   * The company this chat is about, null for the standalone chat. Handed to
   * the composer, which decides from it what it may promise: a chat about one
   * company does not search people (`useIsPeopleSearchOffered`).
   */
  companyTargeted?: CompanyDTO | null;
  /** Text the composer takes back: a creation that did not go through. */
  draftToRestore?: { text: string; attempt: number } | null;
  /** The composer took the draft back. */
  onDraftRestored?: () => void;
}) {
  const { t } = useTranslation();
  const { isMobile } = useDeviceFormat();
  const { stopStreamBeforeSend } = useChatStream();
  const chatInputRef = useRef<ChatTextInputRef>(null);
  const glide = useComposerGlide();
  const isCompanyTargeted = !!companyTargeted;
  // Mirrors the composer's armed mode so the pill that is on shows it. The
  // composer owns the mode and reports back, since it also clears it on its
  // own — after a send, on Backspace, or when a tool is forced.
  const [requestMode, setRequestMode] = useState<ChatRequestMode | null>(null);

  // The welcome screen of the standalone chat. The company chat opens on its
  // own question. The phone's welcome is `ChatNewMobileHome`, returned below:
  // `!isMobile` keeps the cascade timer above from running for it.
  const isHome = !isCompanyTargeted && !isMobile;

  // The company chat's own entry screen (#707), desktop only like the welcome
  // screen: the mobile chat keeps the screen it had.
  const isCompanyHome = isCompanyTargeted && isMobile === false;

  const [isCascading, setIsCascading] = useState(true);
  useEffect(() => {
    if (!isHome) return;
    const timeout = window.setTimeout(
      () => setIsCascading(false),
      HOME_CASCADE_END_MS,
    );
    return () => window.clearTimeout(timeout);
  }, [isHome]);

  // Set on a block's wrapper and inherited by the `suggestion-cascade` items
  // inside it, which add their own stagger to it.
  const cascadeStart = (
    block: keyof typeof HOME_CASCADE_START_MS,
  ): CSSProperties | undefined =>
    isHome && isCascading
      ? ({
          "--cascade-start": `${HOME_CASCADE_START_MS[block]}ms`,
        } as CSSProperties)
      : undefined;

  // Off the welcome screen there is nothing to glide and no tray to measure, so
  // the message goes straight through.
  const handleMessageSent = (params: MemoryMessageToSend) => {
    if (!isHome) return onMessageSent(params);
    glide.start(() => onMessageSent(params));
  };

  // A mode arms the composer rather than sending anything: the user still has
  // to say which company, which person or which target, so the mode lands as
  // an inline prefix and the cursor goes right after it.
  const handleModeToggle = (mode: ChatRequestMode) => {
    chatInputRef.current?.setRequestMode(requestMode === mode ? null : mode);
    chatInputRef.current?.focus();
  };

  const handlePromptSelect = (
    content: string,
    prompt: ConversationMessagePromptInfo,
  ) => {
    stopStreamBeforeSend().then(() => {
      handleMessageSent({
        llmId: "auto",
        text: content,
        filesAttached: [],
        prompt,
      });
    });
  };

  // Fade the copy out under a light blur, leaving the composer as the only
  // element still moving.
  const leavingCopyClassName = cn(
    "transition-[opacity,filter] duration-200 ease-out",
    glide.isLeaving && "opacity-0 blur-[6px]",
  );

  const glideStyle: CSSProperties | undefined = isHome
    ? {
        transitionDuration: `${COMPOSER_GLIDE_DURATION_MS}ms`,
        transform: glide.isLeaving
          ? `translateY(${glide.distance}px)`
          : undefined,
      }
    : undefined;

  // Until the format is known, a company chat paints nothing: the generic
  // screen would be torn down on desktop the next frame, its composer with it,
  // and the composer's own reads (`/prompts`, the preferences) asked twice.
  if (isCompanyTargeted && isMobile === undefined) {
    return null;
  }

  if (isCompanyHome) {
    return (
      <ChatCompanyHome
        company={companyTargeted}
        onPromptSelect={handlePromptSelect}
        disabled={streamInProgress}
        renderComposer={() => (
          <ChatTextInput
            ref={chatInputRef}
            onMessageSent={handleMessageSent}
            streamInProgress={streamInProgress}
            initialCompanyContext={companyTargeted}
            // The chat already knows which company it is about.
            isCompanyIdentifierShortcutEnabled={false}
            draftToRestore={draftToRestore}
            onDraftRestored={onDraftRestored}
          />
        )}
      />
    );
  }

  if (isMobile && !isCompanyTargeted) {
    return (
      <ChatNewMobileHome
        onMessageSent={onMessageSent}
        streamInProgress={streamInProgress}
        draftToRestore={draftToRestore}
        onDraftRestored={onDraftRestored}
      />
    );
  }

  return (
    // On the welcome screen, three rows: what reads before the composer, the
    // composer, what reads after it. The outer two share the leftover height
    // equally, so the composer sits at the exact middle of the screen whatever
    // stacks above or below it. A side taller than its half (a short window)
    // grows its row instead of overflowing, and only then does the composer
    // leave the middle. The company chat keeps the single centered stack it
    // had: the three blocks, centered together.
    <div
      ref={glide.rootRef}
      className={cn(
        "w-full h-full px-4",
        isHome
          ? "grid grid-rows-[1fr_auto_1fr] justify-items-center"
          : "flex flex-col items-center justify-center",
        glide.isLeaving && "pointer-events-none",
      )}
      style={
        isHome
          ? ({
              "--cascade-duration": `${HOME_CASCADE_DURATION_MS}ms`,
              "--cascade-step": `${HOME_CASCADE_STEP_MS}ms`,
            } as CSSProperties)
          : undefined
      }
    >
      <div
        className={cn(
          "flex flex-col w-full max-w-[800px] gap-4 pb-4 text-center",
          isHome && "self-end",
        )}
      >
        <div className={leavingCopyClassName}>
          {/* On an inner node: the cascade holds its end state, which would
              override the wrapper's fade out as the composer leaves. */}
          {isHome ? (
            <div className="suggestion-cascade" style={cascadeStart("greeting")}>
              <ChatNewGreeting />
            </div>
          ) : (
            <div className="text-2xl font-bold">
              {t("chat.input.helpRequest")}
            </div>
          )}
        </div>
        {/* Right above the composer: the modes are the first thing to choose,
            and the field they arm is the next thing the eye meets. */}
        {isHome && (
          <div className={leavingCopyClassName} style={cascadeStart("modes")}>
            <ChatRequestModePills
              activeMode={requestMode}
              onToggle={handleModeToggle}
              disabled={streamInProgress}
            />
          </div>
        )}
      </div>

      {/* Mirrors the docked composer's box (max-w-3xl + px-3) so the glide is a
          pure vertical move: the input never changes size on the way down. */}
      <div
        className={cn(
          "w-full",
          isHome
            ? "px-3 max-w-3xl transition-transform ease-[cubic-bezier(0.32,0.72,0,1)]"
            : "max-w-[800px]",
        )}
        style={glideStyle}
      >
        {/* The cascade on an inner node too: the wrapper's own transform is
            the glide, which a held animation would pin in place. */}
        <div
          className={cn("relative", isHome && "suggestion-cascade")}
          style={cascadeStart("composer")}
        >
          <div className="relative z-10">
            <ChatTextInput
              ref={chatInputRef}
              onMessageSent={handleMessageSent}
              streamInProgress={streamInProgress}
              showPlaceholderExamples={isHome}
              initialCompanyContext={companyTargeted}
              // A company's own chat already knows which company it is about.
              isCompanyIdentifierShortcutEnabled={!isCompanyTargeted}
              onRequestModeChange={setRequestMode}
              draftToRestore={draftToRestore}
              onDraftRestored={onDraftRestored}
            />
          </div>
          {/* Out of the flow so it neither offsets the centered layout nor is
              seen before the composer starts moving: it only measures the
              landing point, then fades in as the composer docks. */}
          {isHome && (
            <ChatComposerDisclaimer
              ref={glide.disclaimerRef}
              className={cn(
                "absolute inset-x-0 top-full transition-opacity duration-300 ease-out pointer-events-none",
                glide.isLeaving ? "opacity-100" : "opacity-0",
              )}
            />
          )}
        </div>
      </div>

      {/* Not rendered when it would be empty — nothing to show on the mobile
          chat, and everything hidden in a short window: its top padding alone
          would push a centered stack off the middle. Nor while the format is
          unknown: on desktop the company screen above replaces this block,
          which would otherwise ask for the tactics for one frame for nothing. */}
      {(isHome || (isCompanyTargeted && isMobile !== undefined)) && (
        <div
          className={cn(
            "flex-col items-center w-full gap-6 pt-6 hidden [@media(min-height:700px)]:flex",
            isHome && "self-start",
          )}
        >
          <div
            className={cn(
              "w-full min-h-0",
              isHome ? "max-w-[800px]" : "max-w-[1000px]",
              leavingCopyClassName,
            )}
            style={cascadeStart("tactics")}
          >
            <ChatPromptsSuggestions
              onPromptSelect={handlePromptSelect}
              disabled={streamInProgress}
              variant={isHome ? "compact" : "full"}
            />
          </div>

          {/* The standalone home screen only: a company-targeted chat already
              opens on the one company its user came for. */}
          {isHome && (
            <div
              className={cn("w-full max-w-[800px]", leavingCopyClassName)}
              style={cascadeStart("recentCompanies")}
            >
              <ChatRecentCompanies />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
